import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import PhotoUploader from './PhotoUploader';
import { useInstaller } from '../../contexts/InstallerContext';
import { useJobs } from '../../hooks/useJobs';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase-init';
import { sendIssueReportedEmail } from '../../services/installer-notification-service';

export default function ReportIssueForm({ isOpen, onClose, job }) {
  const navigate = useNavigate();
  const installer = useInstaller();
  const { refreshJobs } = useJobs();
  
  const [showPhotoUploader, setShowPhotoUploader] = useState(false);
  const [issueType, setIssueType] = useState('');
  const [explanation, setExplanation] = useState('');
  const [needsRescheduling, setNeedsRescheduling] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const issuePhotos = job.installerPhotos?.filter(p => p.type === 'issue') || [];

  const issueTypes = [
    'Missing materials',
    'Wrong size/color',
    'Damage discovered',
    'Customer not ready',
    'Weather delay',
    'Other'
  ];

  const handlePhotoUploadComplete = () => {
    refreshJobs();
  };

  const validate = () => {
    const newErrors = {};

    if (!issueType) {
      newErrors.issueType = 'Please select an issue type';
    }

    if (!explanation.trim()) {
      newErrors.explanation = 'Please describe the issue';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);

    try {
      const jobRef = doc(db, 'jobs', job.id);
      
      await updateDoc(jobRef, {
        flagged: true,
        flagReason: issueType,
        flagNote: explanation,
        flaggedBy: installer.name,
        flaggedByUid: installer.uid,
        flaggedAt: Timestamp.now(),
        needsRescheduling: needsRescheduling
      });

      await sendIssueReportedEmail(job, installer, {
        issueType,
        explanation,
        needsRescheduling,
        photoCount: issuePhotos.length
      });

      console.log('✅ Issue reported successfully');
      
      onClose();
      navigate('/installer');
    } catch (error) {
      console.error('❌ Error reporting issue:', error);
      setErrors({ submit: 'Failed to report issue. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="⚠️ Report Issue"
        size="large"
      >
        <div style={{
          maxHeight: '70vh',
          overflowY: 'auto',
          padding: '24px'
        }}>
          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#2b2b2b',
            marginBottom: '8px'
          }}>
            #{job.contractNumber} - {job.customerName || job.name}
          </div>

          <div style={{
            fontSize: '14px',
            color: '#666',
            marginBottom: '24px'
          }}>
            Report any issues with this job. The office will be notified immediately.
          </div>

          <div style={{ marginBottom: '24px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#2b2b2b',
              marginBottom: '12px'
            }}>
              Issue Type *
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              {issueTypes.map((type) => (
                <label
                  key={type}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px',
                    border: `2px solid ${issueType === type ? '#f97316' : '#e5e5e5'}`,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    background: issueType === type ? 'rgba(249, 115, 22, 0.05)' : 'white',
                    transition: 'all 0.2s',
                    minHeight: '44px'
                  }}
                >
                  <input
                    type="radio"
                    name="issueType"
                    value={type}
                    checked={issueType === type}
                    onChange={(e) => setIssueType(e.target.value)}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: '#f97316'
                    }}
                  />
                  <span style={{
                    fontSize: '15px',
                    fontWeight: '500',
                    color: issueType === type ? '#f97316' : '#2b2b2b'
                  }}>
                    {type}
                  </span>
                </label>
              ))}
            </div>

            {errors.issueType && (
              <div style={{
                color: '#dc2626',
                fontSize: '13px',
                marginTop: '8px',
                fontWeight: '500'
              }}>
                {errors.issueType}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#2b2b2b',
              marginBottom: '8px'
            }}>
              Explanation *
            </label>
            <textarea
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder="Please describe the issue in detail..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '12px',
                border: '2px solid #e5e5e5',
                borderRadius: '12px',
                fontSize: '16px',
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#f97316'}
              onBlur={(e) => e.target.style.borderColor = '#e5e5e5'}
            />

            {errors.explanation && (
              <div style={{
                color: '#dc2626',
                fontSize: '13px',
                marginTop: '8px',
                fontWeight: '500'
              }}>
                {errors.explanation}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '24px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#2b2b2b',
              marginBottom: '12px'
            }}>
              Photos (optional)
            </div>

            {issuePhotos.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                marginBottom: '12px'
              }}>
                {issuePhotos.map((photo, index) => (
                  <div key={index} style={{
                    position: 'relative',
                    paddingBottom: '100%',
                    background: '#f5f2ed',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    <img
                      src={photo.url}
                      alt={`Issue ${index + 1}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowPhotoUploader(true)}
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(249, 115, 22, 0.1)',
                border: '2px dashed #f97316',
                borderRadius: '12px',
                color: '#f97316',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                minHeight: '44px'
              }}
            >
              + Upload Issue Photos ({issuePhotos.length})
            </button>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px',
              border: '2px solid #e5e5e5',
              borderRadius: '12px',
              cursor: 'pointer',
              background: needsRescheduling ? 'rgba(249, 115, 22, 0.05)' : 'white',
              minHeight: '44px'
            }}>
              <input
                type="checkbox"
                checked={needsRescheduling}
                onChange={(e) => setNeedsRescheduling(e.target.checked)}
                style={{
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  accentColor: '#f97316'
                }}
              />
              <div>
                <div style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#2b2b2b',
                  marginBottom: '2px'
                }}>
                  Needs Rescheduling?
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#666'
                }}>
                  Check this if the job needs to be rescheduled
                </div>
              </div>
            </label>
          </div>

          {errors.submit && (
            <div style={{
              padding: '12px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              color: '#dc2626',
              fontSize: '14px',
              marginBottom: '16px'
            }}>
              {errors.submit}
            </div>
          )}

          <div style={{
            padding: '12px',
            background: 'rgba(249, 115, 22, 0.1)',
            border: '1px solid rgba(249, 115, 22, 0.3)',
            borderRadius: '12px',
            fontSize: '13px',
            color: '#c2410c',
            fontWeight: '500'
          }}>
            ⚠️ The office will be notified immediately. The job status will NOT change and customers will NOT be contacted.
          </div>
        </div>

        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e5e5',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          background: '#fafafa'
        }}>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
            style={{ minHeight: '44px' }}
          >
            Cancel
          </Button>
          
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              background: '#f97316',
              minHeight: '44px'
            }}
          >
            {submitting ? 'Reporting...' : 'Submit Issue Report'}
          </Button>
        </div>
      </Modal>

      {showPhotoUploader && (
        <PhotoUploader
          isOpen={showPhotoUploader}
          onClose={() => setShowPhotoUploader(false)}
          jobId={job.id}
          photoType="issue"
          onUploadComplete={handlePhotoUploadComplete}
        />
      )}
    </>
  );
}